import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { urlFor } from '@/lib/sanity';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { ArrowRight } from 'lucide-react';

interface FeaturedCardProps {
  post: {
    title: string;
    slug: { current: string };
    excerpt: string;
    featuredImage?: any;
    category?: { title: string; slug: { current: string } };
    publishedAt: string;
    readTime?: string;
  };
}

export default function FeaturedCard({ post }: FeaturedCardProps) {
  const publishDate = format(new Date(post.publishedAt), 'MMMM d, yyyy');

  return (
    <Link 
      href={`/blog/${post.slug.current}`}
      className="group grid grid-cols-1 lg:grid-cols-2 bg-white rounded-[32px] border border-border overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-charcoal/5 hover:-translate-y-1"
    >
      {/* Image Area */}
      <div className="relative aspect-[3/2] lg:aspect-auto w-full min-h-[300px] overflow-hidden bg-deep-green">
        {post.featuredImage ? (
          <Image
            src={typeof post.featuredImage === 'string' ? post.featuredImage : urlFor(post.featuredImage).url()}
            alt={(typeof post.featuredImage === 'string' ? "" : post.featuredImage.alt) || post.title}
            fill
            sizes="(max-width: 1024px) 95vw, 650px"
            className="object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-deep-green to-warm-green flex items-center justify-center opacity-60">
            <span className="font-serif italic text-white/20 text-6xl">Laurel</span>
          </div>
        )}
      </div>


      {/* Content Area */}
      <div className="flex flex-col justify-center p-8 md:p-12 lg:p-16">
        <div className="flex items-center gap-3 mb-8">
          <Badge className="bg-bloom-gold text-white border-0 py-1.5 px-4 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] leading-none shadow-sm">
            Featured
          </Badge>
          {post.category && (
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-bloom-gold-dark leading-none pt-[1px]">
              {post.category.title}
            </span>
          )}
        </div>

        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal text-charcoal mb-6 group-hover:text-deep-green transition-colors leading-[1.15]">
          {post.title}
        </h2>
        
        <p className="font-sans text-lg text-muted-foreground mb-10 leading-relaxed max-w-xl">
          {post.excerpt}
        </p>

        
        <div className="flex flex-col sm:flex-row sm:items-center gap-6 mt-auto">
          <div className="flex items-center gap-4 py-2 px-4 rounded-full bg-cream/50 border border-border w-fit">
            <span className="text-xs text-muted-foreground font-bold uppercase tracking-widest" suppressHydrationWarning>
              {publishDate}
            </span>
            <span className="w-1 h-1 rounded-full bg-bloom-gold" />
            <span className="text-xs text-muted-foreground font-bold uppercase tracking-widest">
              {post.readTime?.toString().toUpperCase().includes('READ') ? post.readTime : `${post.readTime} MIN READ`}
            </span>
          </div>
          
          <span className="font-sans text-deep-green font-bold flex items-center gap-2 group-hover:translate-x-2 transition-transform duration-300">
            Read article <ArrowRight size={18} />
          </span>
        </div>

      </div>
    </Link>
  );
}
